import React, { useEffect, useRef, useState } from "react";
import Search from "./Search";
import { useGetMeQuery } from "../../redux/api/userApi";
import { useDispatch, useSelector } from "react-redux";
import { setIsAuthenticated, setUser } from "../../redux/features/userSlice";
import { Link, useNavigate } from "react-router-dom";
import { useLazyLogoutQuery } from "../../redux/api/authApi";
import { PRODUCT_CATEGORIES } from "../../constants/constants";

const Header = () => {
    const navigate = useNavigate();
    useGetMeQuery();
    const [logout] = useLazyLogoutQuery();
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const { user } = useSelector((state) => state.auth);
    const { cartItems } = useSelector((state) => state.cart);

    useEffect(() => {
        const closeOnOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setCategoryDropdownOpen(false);
            }
        };

        document.addEventListener("click", closeOnOutsideClick);
        return () => document.removeEventListener("click", closeOnOutsideClick);
    }, []);

    useEffect(() => {
        if (!menuOpen) {
            setCategoryDropdownOpen(false);
        }
    }, [menuOpen]);

    const handleCategoryNavigate = (category) => {
        const params = new URLSearchParams();
        params.set("filters", "1");
        params.set("category", category);
        params.set("page", "1");

        setCategoryDropdownOpen(false);
        setMenuOpen(false);
        navigate({ pathname: "/", search: `?${params.toString()}` });
    };

    const logoutHandler = async () => {
        try {
            await logout().unwrap();
        } catch (e) {
            // ignore
        } finally {
            dispatch(setUser(null));
            dispatch(setIsAuthenticated(false));
            navigate(0);
        }
    };

    return (
        <nav className="navbar jewellery-navbar fixed-top shadow-sm navbar-expand-lg px-3">
            <div className="container-fluid gx-0">
                {/* Brand */}
                <a className="navbar-brand fw-bold mb-0 me-auto" style={{ fontSize: '1.1rem' }} href="/">
                    Trinket of Treasure
                </a>

                {/* Cart and Toggler */}
                <div className="d-flex align-items-center gap-3">
                    <Link
                        to="/cart"
                        className="cart-link position-relative text-decoration-none d-lg-none"
                        style={{ fontSize: '1.1rem' }}
                    >
                        <i className="fa fa-shopping-bag"></i>
                        {cartItems.length > 0 && <span className="cart-count">{cartItems.length}</span>}
                    </Link>

                    {/* Hamburger Button */}
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-controls="navbarContent"
                        aria-expanded={menuOpen}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                </div>

                {/* Collapsible Content */}
                <div
                    className={`collapse navbar-collapse ${menuOpen ? "show" : ""}`}
                    id="navbarContent"
                >
                    {/* Center Search */}
                    <div className="mx-auto my-3 my-lg-0 px-3 d-flex justify-content-center w-60 w-lg-50">
                        <div className="search-inner w-100" style={{ maxWidth: 720 }}>
                            <Search />
                        </div>
                    </div>

                    {/* Mobile-only category dropdown */}
                    <div className="w-100 mt-3 d-lg-none" ref={dropdownRef}>
                        <button
                            type="button"
                            className={`mobile-category-toggle ${categoryDropdownOpen ? "open" : ""}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setCategoryDropdownOpen((prev) => !prev);
                            }}
                        >
                            <span>Categories</span>
                            <i className={`fa fa-chevron-${categoryDropdownOpen ? "up" : "down"}`}></i>
                        </button>
                        <div className={`mobile-category-menu ${categoryDropdownOpen ? "show" : ""}`}>
                            {PRODUCT_CATEGORIES?.map((category) => (
                                <button
                                    key={category}
                                    type="button"
                                    className="mobile-category-item"
                                    onClick={() => handleCategoryNavigate(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>


                    {/* Right Section */}
                    <div className="d-flex align-items-center ms-auto mt-3 mt-lg-0">
                        {/* Cart - Hidden on mobile, shown on desktop */}
                        <Link
                            to="/cart"
                            className="cart-link position-relative me-3 text-decoration-none d-none d-lg-block"
                        >
                            <i className="fa fa-shopping-bag fs-5"></i>
                            {cartItems.length > 0 && <span className="cart-count">{cartItems.length}</span>}
                        </Link>

                        {/* User Menu */}
                        {user ? (
                            <div className="dropdown">
                                <button
                                    className="btn profile-btn d-flex align-items-center"
                                    type="button"
                                    id="userDropdown"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    <img
                                        src={user?.avatar ? user?.avatar?.url : "/images/default_avatar.jpg"}
                                        alt="User Avatar"
                                        className="profile-avatar me-2"
                                    />
                                    <span className="d-inline">Hi, {user?.name || "User"}</span>
                                    <i className="fa fa-caret-down ms-2"></i>
                                </button>
                                <ul
                                    className="dropdown-menu dropdown-menu-end glass-dropdown"
                                    aria-labelledby="userDropdown"
                                >
                                    {user?.role === "admin" && (
                                        <li>
                                            <Link className="dropdown-item" to="/admin/dashboard">
                                                💎 Dashboard
                                            </Link>
                                        </li>
                                    )}
                                    <li>
                                        <Link className="dropdown-item" to="/me/orders">
                                            💍 My Orders
                                        </Link>
                                    </li>
                                    <li>
                                        <Link className="dropdown-item" to="/me/profile">
                                            👤 My Profile
                                        </Link>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item text-danger"
                                            onClick={logoutHandler}
                                        >
                                            🚪 Logout
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        ) : (
                            <Link to="/login" className="btn ms-3 View-btn">
                                Sign In
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Header;